// ChildWedding.js
import React, { useState, useEffect } from 'react';
import { submitChildWeddingForm } from '../services/api';
import '../css/ChildWeddingForm.css';
import ChildWeddingFormTranslations from '../components/ChildWeddingFormTranslations';

const ChildWedding = ({ onBackToGoals, onGoalComplete, parentCurrentAge, parentRetirementAge }) => {

  const currentLanguage = 'en';
  const translations = ChildWeddingFormTranslations[currentLanguage];
  
  // Calculate parent's years to retirement
  const parentYearsToRetirement = parentRetirementAge - parentCurrentAge;
  console.log('ChildWedding props:', { parentCurrentAge, parentRetirementAge }); // Debug log
  
  // Form states
  const [numChildren, setNumChildren] = useState('');
  const [children, setChildren] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [expectedROR, setExpectedROR] = useState(12);
  
  // Results states
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [totalSIP, setTotalSIP] = useState(0);
  
  // Error states
  const [errors, setErrors] = useState({});
  const [childrenErrors, setChildrenErrors] = useState([]);

  // Initialize children when numChildren changes
  useEffect(() => {
    const num = parseInt(numChildren);
    if (!isNaN(num) && num >= 0 && num <= 5) {
      setChildren(Array(num).fill().map(() => ({
        name: '',
        gender: translations.data.childGenderOptions[0]?.value || 'Male',
        age: '',
        goldCost: '',
        weddingCost: '',
      })));
      
      setChildrenErrors(Array(num).fill({}));
    }
  }, [numChildren]);

  const handleChildChange = (index, field, value) => {
    const updatedChildren = [...children];
    updatedChildren[index] = {
      ...updatedChildren[index],
      [field]: value
    };
    setChildren(updatedChildren);

    const updatedChildErrors = [...childrenErrors];
    if (updatedChildErrors[index]?.[field]) {
      updatedChildErrors[index] = { 
        ...updatedChildErrors[index], 
        [field]: undefined 
      };
      setChildrenErrors(updatedChildErrors);
    }
  };

  const validateChildrenDetails = () => {
    let valid = true;
    const newChildrenErrors = children.map(() => ({}));

    
    // Validate expected ROR
    if (isNaN(expectedROR) || expectedROR <= 0) {
      setErrors(prev => ({...prev, expectedROR: 'Expected return must be a positive number'}));
      valid = false;
    } else {
      setErrors(prev => ({...prev, expectedROR: undefined}));
    }
    
    // Validate parent retirement data
    if (isNaN(parentYearsToRetirement) || parentYearsToRetirement <= 0) {
      setErrors(prev => ({...prev, retirement: 'Parent retirement horizon must be positive'}));
      valid = false;
    } else {
      setErrors(prev => ({...prev, retirement: undefined}));
    }

        // Validate parent retirement data
        console.log('Validating retirement:', { parentYearsToRetirement, parentCurrentAge, parentRetirementAge });
        if (isNaN(parentYearsToRetirement) ){
          setErrors(prev => ({...prev, retirement: 'Invalid retirement calculation'}));
          valid = false;
        } else if (parentYearsToRetirement <= 0) {
          setErrors(prev => ({...prev, retirement: 'Parent retirement horizon must be positive'}));
          valid = false;
        } else {
          setErrors(prev => ({...prev, retirement: undefined}));
        }
    
    children.forEach((child, index) => {
      let childErrors = {};

      if (!child.name.trim()) {
        childErrors.name = translations.errors.childNameRequired || 'Child name is required';
        valid = false;
      }

      const age = parseInt(child.age);
      if (isNaN(age) || age < 1 || age > 26) {
        childErrors.age = translations.errors.invalidChildAge || 'Age must be between 1 and 26';
        valid = false;
      }

      if (isNaN(parseFloat(child.goldCost)) || parseFloat(child.goldCost) < 0) {
        childErrors.goldCost = translations.errors.invalidGoldCost || 'Gold Cost must be a positive number';
        valid = false;
      }
      
      if (isNaN(parseFloat(child.weddingCost)) || parseFloat(child.weddingCost) < 0) {
        childErrors.weddingCost = translations.errors.invalidWeddingCost || 'Wedding Cost must be a positive number';
        valid = false;
      }

      newChildrenErrors[index] = childErrors;
    });

    setChildrenErrors(newChildrenErrors);
    return valid;
  };

  // Calculate SIP for a future cost
  const calculateSIP = (futureValue, years) => {
    if (years <= 0) return 0;
    
    const months = years * 12;
    const monthlyRate = expectedROR / 100 / 12;
    
    // Formula: SIP = FV * r / [(1+r)^n - 1]
    return futureValue * monthlyRate / (Math.pow(1 + monthlyRate, months) - 1);
  };

  const calculateWedding = () => {
    if (!validateChildrenDetails()) {
      alert(translations.errors.validationFailed || "Please correct the form errors.");
      return;
    }

    let totalCost = 0;
    let totalSIP = 0;
    const calculatedResults = [];

    children.forEach(child => {
      const age = parseInt(child.age);
      const childYearsToWedding = 27 - age;
      
      // Use the MINIMUM of parent's retirement horizon or child's wedding timeline
      const investmentHorizon = Math.min(
        parentYearsToRetirement,
        childYearsToWedding
      );
      
      const goldInflationRate = 0.125; // 12.5%
      const weddingInflationRate = 0.06; // 6%

      // Calculate future costs using investmentHorizon
      const futureGoldCost = parseFloat(child.goldCost) * Math.pow(1 + goldInflationRate, investmentHorizon);
      const futureWeddingCost = parseFloat(child.weddingCost) * Math.pow(1 + weddingInflationRate, investmentHorizon);
      
      // Calculate SIPs using investmentHorizon
      const sipForGold = calculateSIP(futureGoldCost, investmentHorizon);
      const sipForWedding = calculateSIP(futureWeddingCost, investmentHorizon);
      const totalSIPPerChild = sipForGold + sipForWedding;

      const childTotal = futureGoldCost + futureWeddingCost;
      totalCost += childTotal;
      totalSIP += totalSIPPerChild;
      
      calculatedResults.push({
        name: child.name,
        age: child.age,
        yearsToWedding: childYearsToWedding,
        futureGoldCost,
        futureWeddingCost,
        childTotal,
        sipForGold,
        sipForWedding,
        totalSIPPerChild,
        investmentHorizon,
        parentYearsToRetirement
      });
    });

    setTotalCost(totalCost);
    setTotalSIP(totalSIP);
    setResults(calculatedResults);
    setShowResults(true);
  };

  const handleSubmitForm = async () => {
    if (!showResults) {
      alert("Please calculate costs before submitting");
      return;
    }

    try {
      const formData = {
        totalWeddingExpenses: totalCost,
        children: children.map(child => ({
          name: child.name,
          gender: child.gender,
          age: parseInt(child.age),
          currentGoldCost: parseFloat(child.goldCost),
          currentWeddingCost: parseFloat(child.weddingCost),
        })),
      };

      // Call onGoalComplete with the structured data
      onGoalComplete(formData);
      console.log("Child wedding form submitted successfully:", formData);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again.');
    }
  };

  // Format currency in Indian style
  const formatCurrency = (amount) => {
    return '₹' + amount.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  return (
    <div className="child-wedding-form-container">
      <div className="child-wedding-form">
        <h2>{translations.labels.title || "Children's Wedding Planning"}</h2>
        
        <div className="form-group">
          <label>{translations.labels.expected_return || "Expected Return (%)"}:</label>
          <input
            type="number"
            min="0"
            step="0.1"
            value={expectedROR}
            onChange={(e) => setExpectedROR(parseFloat(e.target.value) || 0)}
            className="form-input"
            placeholder={translations.placeholders.expected_return}
          />
          {errors.expectedROR && <div className="error">{errors.expectedROR}</div>}
          {errors.retirement && <div className="error">{errors.retirement}</div>}
        </div>

        <div className="form-group">
          <label>{translations.labels.number_of_children}:</label>
          <select
            value={numChildren}
            onChange={(e) => setNumChildren(e.target.value)}
            className="form-select"
          >
            <option value="">{translations.placeholders.number_of_children}</option>
            {[0, 1, 2, 3, 4, 5].map(num => (
              <option key={num} value={num}>{num}</option>
            ))}
          </select>
          {errors.numChildren && <div className="error">{errors.numChildren}</div>}
        </div>

        {children.length > 0 && (
          <div className="children-details-section">
            <h3>{translations.labels.children_details || "Children's Details"}</h3>
            
            {children.map((child, index) => (
              <div key={index} className="child-section">
                <h4>{translations.labels.child_number || "Child"} {index + 1}</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label>{translations.labels.child_name}:</label>
                    <input
                      type="text"
                      value={child.name}
                      onChange={(e) => handleChildChange(index, 'name', e.target.value)}
                      placeholder={translations.placeholders.child_name}
                      className="form-input"
                    />
                    {childrenErrors[index]?.name && 
                      <div className="error">{childrenErrors[index].name}</div>}
                  </div>

                  <div className="form-group">
                    <label>{translations.labels.gender}:</label>
                    <select
                      value={child.gender}
                      onChange={(e) => handleChildChange(index, 'gender', e.target.value)}
                      className="form-select"
                    >
                      {translations.data.childGenderOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{translations.labels.age} (1-26):</label>
                    <input
                      type="number"
                      min="1"
                      max="26"
                      value={child.age}
                      onChange={(e) => handleChildChange(index, 'age', e.target.value)}
                      className="form-input"
                      placeholder={translations.placeholders.age}
                    />
                    {childrenErrors[index]?.age && 
                      <div className="error">{childrenErrors[index].age}</div>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{translations.labels.currentGoldCostInr}:</label>
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      value={child.goldCost}
                      onChange={(e) => handleChildChange(index, 'goldCost', e.target.value)}
                      className="form-input"
                      placeholder={translations.placeholders.currentGoldCostInr}
                    />
                    {childrenErrors[index]?.goldCost && 
                      <div className="error">{childrenErrors[index].goldCost}</div>}
                  </div>

                  <div className="form-group">
                    <label>{translations.labels.currentWeddingCostInr}:</label>
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      value={child.weddingCost}
                      onChange={(e) => handleChildChange(index, 'weddingCost', e.target.value)}
                      className="form-input"
                      placeholder={translations.placeholders.currentWeddingCostInr}
                    />
                    {childrenErrors[index]?.weddingCost && 
                      <div className="error">{childrenErrors[index].weddingCost}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        
{showResults && results.length > 0 && (
          <div className="results-section">
            <h3>{translations.labels.results || "Wedding Planning Results"}</h3>
            
            {results.map((result, index) => (
              <div key={index} className="child-result">
                <h4>
                  {result.name || `${translations.labels.child} ${index + 1}`} 
                  <span> ({translations.labels.current_age}: {result.age})</span>
                </h4>
                
                <div className="result-row">
                  <div className="result-column">
                    <div className="result-detail">
                      <span>{translations.labels.future_gold_cost || "Estimated Gold Cost"}:</span>
                      <strong> {formatCurrency(result.futureGoldCost)}</strong>
                    </div>
                    
                    <div className="result-detail">
                      <span>{translations.labels.future_wedding_cost || "Estimated Wedding Cost"}:</span>
                      <strong> {formatCurrency(result.futureWeddingCost)}</strong>
                    </div>
                    
                    <div className="result-detail">
                      <span>Years to Wedding:</span>
                      <strong> {result.yearsToWedding} years</strong>
                    </div>
                    
                    <div className="result-detail total">
                      <span>{translations.labels.total_child_cost || "Total Cost"}:</span>
                      <strong> {formatCurrency(result.childTotal)}</strong>
                    </div>
                  </div>
                  
                  <div className="result-column">
                    <div className="result-detail">
                      <span>SIP for Gold:</span>
                      <strong> {formatCurrency(result.sipForGold)}</strong>
                    </div>
                    
                    <div className="result-detail">
                      <span>SIP for Wedding:</span>
                      <strong> {formatCurrency(result.sipForWedding)}</strong>
                    </div>
                    
                    <div className="result-detail">
                      <span>Investment Horizon:</span>
                      <strong> {result.investmentHorizon} years</strong>
                      <div className="horizon-explanation">
                        (Min of: Parent Retirement {result.parentYearsToRetirement}y, Child Wedding {result.yearsToWedding}y)
                      </div>
                    </div>
                    
                    <div className="result-detail total">
                      <span>Total SIP per Child:</span>
                      <strong> {formatCurrency(result.totalSIPPerChild)}</strong>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            <div className="total-result">
              <div className="result-row">
                <div className="result-column">
                  <h3>
                    {translations.labels.total_wedding_cost || "Total Estimated Expenses"}: 
                    <strong> {formatCurrency(totalCost)}</strong>
                  </h3>
                </div>
                <div className="result-column">
                  <h3>
                    {translations.labels.total_sip || "Total Monthly SIP"}: 
                    <strong> {formatCurrency(totalSIP)}</strong>
                  </h3>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="action-buttons">
          <button className="submit-button" onClick={calculateWedding}>
            {translations.labels.calculate1 || "Calculate"}
          </button>
          <button className="submit-button" onClick={onBackToGoals}>
            ← {translations.labels.back_to_goals || "Back to Goals"}
          </button>
          <button
            className="submit-button"
            onClick={handleSubmitForm}
            disabled={!showResults}
          >
            {translations.labels.next || "Next"}
          </button>
        </div>

   
           <div className="assumptions">
            <strong>{translations.labels.assumptions || "Assumptions"}:</strong>
            <ul>
              <li>{translations.labels.goldInflation || "Gold Inflation"}: 12.5%</li>
              <li>{translations.labels.weddingInflation || "Wedding Inflation"}: 6%</li>
              <li>{translations.labels.weddingAge || "Wedding Age"}: 27 {translations.labels.years || "years"}</li>
              <li>{translations.labels.expectedReturn || "Expected Return"}: {expectedROR}%</li>
              <li>Parent Retirement Age: {parentRetirementAge || 'N/A'}</li>
              <li>Parent Current Age: {parentCurrentAge ?? 'N/A'}</li>
              <li>Investment Horizon: MIN(Parent Retirement, Child Wedding)</li>
            </ul>
          </div>
      </div>
    </div>
  );
};

export default ChildWedding;